import moment from 'moment'
import Config from 'react-native-config'
import DeviceInfo from 'react-native-device-info'
import { OnboardingExperience, Sensor, Subscription, SurveysConfigKind } from '@src/types'
import { Storage } from '@src/utils'
import { shouldRequestInAppReview } from '@src/utils/appReview'
import { AllSurveyLinksQuery } from '@src/graphql/fetchQuestionnaire.generated'
import { lastFinishedSurveyOfKind } from '@src/screens/Questionnaire/utils'
import { BuildType } from '@src/config/app'
import {
  MENTAL_HEALTH_SERVICES_INTEREST_SURVEY_DELAY_DAYS,
  VIDEO_CALLS_UPSELL_MODAL_DELAY_DAYS,
} from '../../constants'

export const getStoreReviewPromptStateInteractions = ({
  sensor,
}: {
  sensor: Sensor | undefined
}) => ({
  shouldShow: () => {
    const lastPromptDate = Storage.get<string>(Storage.LAST_REVIEW_PROMPT_DATE_KEY)
    const hasLeftReview = Storage.get(Storage.REVIEW_LEFT_KEY, false)
    const reviewPromptsCount = Storage.get(Storage.REVIEW_PROMPTS_COUNT_KEY, 0)

    return shouldRequestInAppReview({
      hasLeftReview,
      reviewPromptsCount,
      lastPromptDate,
      sensorExpirationTime: sensor?.expirationTime,
    })
  },

  markAsShown: () => {
    const reviewPromptsCount = Storage.get(Storage.REVIEW_PROMPTS_COUNT_KEY, 0)
    Storage.set(Storage.LAST_REVIEW_PROMPT_DATE_KEY, moment().toISOString())
    Storage.set(Storage.REVIEW_PROMPTS_COUNT_KEY, reviewPromptsCount + 1)
  },
})

export const shouldShowWeightLossExperienceOnboarding = ({
  userId,
  onboardingExperience,
  surveyLinks,
  scheduledFirstAppointment,
}: {
  userId?: string
  onboardingExperience: undefined | null | OnboardingExperience
  surveyLinks: AllSurveyLinksQuery['allSurveyLinks']['surveyLinks']
  scheduledFirstAppointment: boolean
}) => {
  const surveyKind = SurveysConfigKind.OnboardingExperienceBasicInformation
  const firstStepSurvey = surveyLinks.find((survey) => survey.survey.kind === surveyKind)
  const flowAlreadyPoppedUp = Storage.get(
    `${Storage.WEIGHT_LOSS_EXPERIENCE_ONBOARDING_KEY}_${userId}`,
  )

  return (
    scheduledFirstAppointment &&
    onboardingExperience?.participating &&
    !firstStepSurvey &&
    !flowAlreadyPoppedUp
  )
}

export const shouldShowVideoCallsSteloUpsellBanner = () => {
  const modalAlreadyPoppedUp = Storage.get(Storage.VIDEO_CALLS_STELO_UPSELL_MODAL_VISITED_KEY)

  return !modalAlreadyPoppedUp
}

export const shouldShowRecurringSubscriptionConsentSurvey = ({
  surveyLinks,
  hasNonCanceledMonthlyCoreSubscription,
  recurringSubscriptionConsentAccepted,
}: {
  surveyLinks: AllSurveyLinksQuery['allSurveyLinks']['surveyLinks']
  hasNonCanceledMonthlyCoreSubscription: boolean
  recurringSubscriptionConsentAccepted: boolean | undefined
}) => {
  if (recurringSubscriptionConsentAccepted === undefined) {
    return false
  }

  if (!hasNonCanceledMonthlyCoreSubscription) {
    return false
  }

  if (recurringSubscriptionConsentAccepted) {
    return false
  }

  if (lastFinishedSurveyOfKind(SurveysConfigKind.CgmPrerequisite, surveyLinks)) {
    // cgm prerequisite survey contains the recurring subscription consent
    return false
  }

  if (lastFinishedSurveyOfKind(SurveysConfigKind.RecurringSubscriptionConsent, surveyLinks)) {
    return false
  }

  return true
}

export const shouldShowCgmMedicalDisclaimerSurvey = ({
  surveyLinks,
  subscriptionIncludesCgm,
  medicalDisclaimerConsentAccepted,
}: {
  surveyLinks: AllSurveyLinksQuery['allSurveyLinks']['surveyLinks']
  subscriptionIncludesCgm: boolean
  medicalDisclaimerConsentAccepted: boolean | undefined
}) => {
  if (medicalDisclaimerConsentAccepted === undefined) {
    return false
  }

  if (medicalDisclaimerConsentAccepted) {
    return false
  }

  if (!subscriptionIncludesCgm) {
    return false
  }

  if (lastFinishedSurveyOfKind(SurveysConfigKind.CgmPrerequisite, surveyLinks)) {
    // cgm prerequisite survey contains the medical disclaimer
    return false
  }

  if (lastFinishedSurveyOfKind(SurveysConfigKind.CgmMedicalDisclaimer, surveyLinks)) {
    return false
  }

  return true
}

export const shouldShowInsuranceQuestionnaire = ({
  userId,
  hasMonthlyCoreSubscription,
  subscriptionIncludesCgm,
  isInsuranceVideoCallProductType,
  hasBundledPaidCallsSubscription,
  showOnboardingInsuranceSurvey,
  insuranceInformationSubmitted,
}: {
  userId: string
  hasMonthlyCoreSubscription: boolean
  subscriptionIncludesCgm: boolean
  isInsuranceVideoCallProductType: boolean
  hasBundledPaidCallsSubscription: boolean
  showOnboardingInsuranceSurvey: boolean
  insuranceInformationSubmitted: boolean
}) => {
  if (!showOnboardingInsuranceSurvey) {
    return false
  }

  if (!isInsuranceVideoCallProductType) {
    return false
  }

  if (!hasMonthlyCoreSubscription) {
    return false
  }

  if (!subscriptionIncludesCgm) {
    return false
  }

  if (hasBundledPaidCallsSubscription) {
    return false
  }

  if (insuranceInformationSubmitted) {
    return false
  }

  const insuranceQuestionnaireVisitedKey = `${Storage.INSURANCE_QUESTIONNAIRE_VISITED_KEY}_${userId}`

  if (Storage.get(insuranceQuestionnaireVisitedKey, false)) {
    // user was already prompted with the insurance questionnaire
    return false
  }

  const appOpenCountKey = `${Storage.APP_OPEN_COUNT_KEY}_${userId}`

  if (Storage.get(appOpenCountKey, 0) <= 1) {
    // show it only on subsequent app openings
    return false
  }

  return true
}

export const shouldShowMedicalDisclaimerSurvey = ({
  surveyLinks,
  hasMedicalSubscription,
  medicalDisclaimerSurveyEnabled,
}: {
  surveyLinks: AllSurveyLinksQuery['allSurveyLinks']['surveyLinks']
  hasMedicalSubscription: boolean
  medicalDisclaimerSurveyEnabled: boolean
}) => {
  if (!hasMedicalSubscription) {
    return false
  }

  if (!medicalDisclaimerSurveyEnabled) {
    return false
  }

  if (lastFinishedSurveyOfKind(SurveysConfigKind.MedicalDisclaimer, surveyLinks)) {
    return false
  }

  return true
}

export const shouldShowMentalHealthServicesInterestSurvey = ({
  mentalHealthServicesInterestSurveyEnabled,
  userId,
  surveyLinks,
}: {
  mentalHealthServicesInterestSurveyEnabled: boolean
  userId?: string
  surveyLinks: AllSurveyLinksQuery['allSurveyLinks']['surveyLinks']
}) => {
  if (!userId || !mentalHealthServicesInterestSurveyEnabled) {
    return false
  }

  const hasStartedSurvey = surveyLinks.some((surveyLink) => {
    return surveyLink.survey.kind === SurveysConfigKind.MentalHealthServicesInterest
  })
  if (hasStartedSurvey) {
    return false
  }

  const firstLogin = Storage.get<string>(`${Storage.APP_FIRST_LOGIN_TIMESTAMP_KEY}_${userId}`)
  if (!firstLogin) {
    return false
  }

  // Show at least 2 days after fresh installation
  if (moment().diff(firstLogin, 'days') < MENTAL_HEALTH_SERVICES_INTEREST_SURVEY_DELAY_DAYS) {
    return false
  }

  return true
}

export const getVideoCallsUpsellRouteParams = ({
  userId,
  lastCoreSubscription,
  hasBundledPaidCallsSubscription,
  isInsuranceVideoCallProductType,
  showOnboardingInsuranceSurvey,
  showAddInsuranceUpsells,
}: {
  userId?: string
  lastCoreSubscription?: Pick<Subscription, 'id' | 'subscribedAt'> | null
  hasBundledPaidCallsSubscription: boolean
  isInsuranceVideoCallProductType: boolean
  showOnboardingInsuranceSurvey: boolean
  showAddInsuranceUpsells: boolean
}) => {
  if (!userId || hasBundledPaidCallsSubscription || !isInsuranceVideoCallProductType) {
    return
  }

  const surveyParams = { name: 'InsuranceQuestionnaire' }
  const modalParams = { name: 'VideoCallsUpsellModal' }

  const visitedRecentSubscriptionKey = `${Storage.VIDEO_CALLS_UPSELL_MODAL_VISITED_RECENT_SUBSCRIPTION}_${userId}`
  const visitedDueToRecentSubscription = Storage.get(visitedRecentSubscriptionKey, false)
  const modalVisitedCountKey = `${Storage.VIDEO_CALLS_UPSELL_MODAL_VISITED_COUNT_KEY}_${userId}`
  const modalVisitedCount = Storage.get(modalVisitedCountKey, 0)

  if (showOnboardingInsuranceSurvey && modalVisitedCount === 0 && !visitedDueToRecentSubscription) {
    Storage.set(visitedRecentSubscriptionKey, true)
    return surveyParams
  }

  const firstLogin = Storage.get<string>(`${Storage.APP_FIRST_LOGIN_TIMESTAMP_KEY}_${userId}`)
  if (firstLogin && moment().diff(firstLogin, 'days') < VIDEO_CALLS_UPSELL_MODAL_DELAY_DAYS) {
    return
  }

  if (lastCoreSubscription && !visitedDueToRecentSubscription) {
    const oneWeekAgo = moment().subtract(1, 'week')
    const subscribedAt = moment(lastCoreSubscription.subscribedAt)

    if (subscribedAt.isSameOrAfter(oneWeekAgo)) {
      if (showOnboardingInsuranceSurvey) {
        Storage.set(visitedRecentSubscriptionKey, true)
        return modalParams
      }

      if (showAddInsuranceUpsells) {
        Storage.set(visitedRecentSubscriptionKey, true)
        return modalParams
      }
    }
  }

  if (modalVisitedCount > 0) {
    return
  }

  if (!showOnboardingInsuranceSurvey) {
    // questionnaire is filled
    return
  }

  if (!showAddInsuranceUpsells) {
    return
  }

  return modalParams
}

export const shouldShowAppRatingModal = async (
  newRatingsEnabled: boolean,
  rateAppEligible: boolean | undefined,
) => {
  if (!newRatingsEnabled || !rateAppEligible) {
    return false
  }

  const currentDate = moment().format('YYYY-MM-DD')
  const usageDays = Storage.get<number>(
    `${Storage.APP_CONSECUTIVE_USAGE_ON_DATE_KEY}_${currentDate}`,
  )
  const build = Config.ENV as BuildType
  const minUsageDays = build === 'production' ? 5 : 1

  if (!usageDays || usageDays < minUsageDays) {
    return false
  }

  const batteryLevel = await DeviceInfo.getBatteryLevel()

  if (batteryLevel > 0 && batteryLevel < 0.2) {
    return false
  }

  return true
}
